// Shared archive helpers — single source for docs, projects AND tasks.
// Previously these were duplicated in docTreeApi.js and ProjectList.jsx; the
// task views reuse them here instead of adding a third copy.

// localStorage key for the "show archived" toggle. Shared across all views, so
// flipping it in one place (docs / projects / tasks) is remembered everywhere.
export const SHOW_ARCHIVED_KEY = 'orbit-show-archived';

export function readShowArchived() {
  try {
    return localStorage.getItem(SHOW_ARCHIVED_KEY) === '1';
  }
  catch {
    return false;
  }
}

export function writeShowArchived(val) {
  try {
    localStorage.setItem(SHOW_ARCHIVED_KEY, val ? '1' : '0');
  }
  catch {
    /* ignore (private mode / disabled storage) */
  }
}

// Human date for the "Archivé le …" badge/tooltip.
export function formatArchiveDate(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Epoch ms for an API `archived` value, or NaN when unusable.
//
// The field is a UNIX timestamp in Drupal, but the decoupled_timestamp formatter
// serializes it with custom_date_format 'Y-m-d\TH:i:s' — so the API sends a
// STRING ("2026-07-16T10:00:00"), never a number. Number() on it returns NaN,
// and every `NaN >= x` comparison is false: that silently emptied the archived
// list as soon as a date filter was set. The numeric branch below only guards
// against a display being reconfigured to emit the raw timestamp.
export function archiveTimestamp(archived) {
  if (archived === null || archived === undefined || archived === '') return NaN;
  if (typeof archived === 'number' || /^\d+$/.test(String(archived))) {
    return Number(archived) * 1000;
  }
  return new Date(archived).getTime();
}

// Epoch ms for a <input type="date"> value ("YYYY-MM-DD"), read as LOCAL midnight.
// `new Date('2026-07-16')` would parse it as UTC while the archive string above
// parses as local time — comparing the two drifts by the timezone offset.
export function localDayStart(ymd) {
  if (!ymd) return NaN;
  const [y, m, d] = String(ymd).split('-').map(Number);
  if (!y || !m || !d) return NaN;
  return new Date(y, m - 1, d).getTime();
}

// Append &include_archived=1 to a decoupled collection URL when archived items
// should be returned. The backend access grants still apply (include_archived
// only lifts the archived-is-hidden filter, never group/node access).
export function withIncludeArchived(url, showArchived) {
  if (!url || !showArchived || url.includes('include_archived')) return url;
  return `${url}${url.includes('?') ? '&' : '?'}include_archived=1`;
}
